import { FlaskConical } from 'lucide-react';
import { IS_SANDBOX } from '@/lib/runtime/feature-flags';
import { useTranslation } from '../../i18n/useTranslation';
import { formatDate } from '@/lib/format/date';
import { lastImportAt } from './lib/sandbox-job';
import { useSandboxJob } from './lib/use-sandbox-job';

/**
 * Permanent bar across the sandbox app: this data is a copy, and the three
 * things that behave differently because of it (no email, no payment, no
 * logos — every storage pointer is stripped by the import). In a sandbox
 * started blank nothing was copied, so it says only what is inert.
 *
 * Rendered inside `<GlobalBanners>`, which owns the sticky positioning and
 * measures the whole stack into `--global-banners-height` — this component is
 * just the bar. `GlobalBanners` sits above every shell, so the auth screens and
 * the setup page carry it too, not only the signed-in app.
 *
 * Not dismissible on purpose: the whole point is that nobody mistakes a
 * sandbox tab for the production one.
 */
export function SandboxBanner() {
  if (!IS_SANDBOX) return null;

  return <ActiveSandboxBanner />;
}

function ActiveSandboxBanner() {
  const { t } = useTranslation('sandbox');
  const job = useSandboxJob();
  const importedAt = lastImportAt(job);

  return (
    <div className="relative flex flex-wrap items-center justify-center gap-x-3 gap-y-1 overflow-hidden border-b-2 border-border bg-riso-amber px-4 py-2.5 text-sm text-white">
      {/* Hazard tape rather than a notification strip: solid, striped and
          impossible to read as part of the page it sits above. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'repeating-linear-gradient(135deg, transparent 0 14px, rgb(255 255 255 / 0.12) 14px 28px)',
        }}
      />
      <span className="relative flex items-center gap-2 rounded-full border-2 border-white/70 bg-white/15 px-3 py-0.5 text-xs font-extrabold uppercase tracking-[0.18em]">
        <FlaskConical className="h-3.5 w-3.5 shrink-0" />
        {t('banner.label')}
        <span className="size-1.5 animate-pulse rounded-full bg-white motion-reduce:animate-none" />
      </span>
      <span className="relative font-semibold">
        {job?.kind === 'blank'
          ? t('banner.bodyBlank')
          : importedAt
            ? t('banner.bodyWithDate', { date: formatDate(importedAt) })
            : t('banner.body')}
      </span>
    </div>
  );
}
